import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { fmtCurrency } from "../lib/format";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Plus, Copy, Check, Eraser, AlertTriangle } from "lucide-react";
import { cn } from "../lib/utils";

/* ISOLATED ADD-ON: Prepayment Calculator.
   Pure estimation utility. Reads CRG/CPA per Geo from a read-only endpoint
   and computes everything locally. NEVER writes to the accounting system. */

const FEE_RATE = 0.025;

const todayStr = () => new Date().toLocaleDateString("en-CA");

const makeOrder = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  geo: "",
  caps: "",
  crg: null, // null = use auto value from Brand data; string = manual override
  cpa: null,
});

const fmtDateLong = (iso) =>
  iso ? new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "";

const fmtNum = (v) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(Number(v) || 0);

const inputClass =
  "h-9 rounded-md border border-input bg-white px-2 font-mono tabular-nums text-sm outline-none transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1";

export const PrepaymentCalculator = ({ open, onOpenChange }) => {
  const [geoParams, setGeoParams] = useState({});
  const [loadingParams, setLoadingParams] = useState(false);
  const [orderDate, setOrderDate] = useState(todayStr());
  const [liveBalance, setLiveBalance] = useState("");
  const [orders, setOrders] = useState([makeOrder()]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoadingParams(true);
    api
      .get("/calculator/geo-params")
      .then((res) => setGeoParams(res.data?.geos || {}))
      .catch(() => setGeoParams({}))
      .finally(() => setLoadingParams(false));
  }, [open]);

  const setOrderField = (id, field, value) => {
    setOrders((prev) =>
      prev.map((o) => {
        if (o.id !== id) return o;
        if (field === "geo") {
          // New Geo: re-prefill CRG/CPA from Brand data (clear manual overrides)
          return { ...o, geo: value, crg: null, cpa: null };
        }
        return { ...o, [field]: value };
      })
    );
  };

  const addOrder = () => setOrders((prev) => [...prev, makeOrder()]);

  const clearAll = () => {
    setOrderDate(todayStr());
    setLiveBalance("");
    setOrders([makeOrder()]);
    setCopied(false);
  };

  const calc = useMemo(() => {
    const balance = Math.max(0, parseFloat(String(liveBalance).replace(/,/g, "")) || 0);
    const parseNum = (v) => {
      const n = parseFloat(String(v).replace(/,/g, ""));
      return isNaN(n) || n < 0 ? 0 : n;
    };
    const rows = orders.map((o) => {
      const geo = (o.geo || "").trim().toUpperCase();
      const caps = parseNum(o.caps);
      const params = geo ? geoParams[geo] : null;
      // Effective values: manual override wins; otherwise auto from Brand data
      const crg = o.crg !== null ? parseNum(o.crg) : params ? params.crg : 0;
      const cpa = o.cpa !== null ? parseNum(o.cpa) : params ? params.cpa : 0;
      const crgAuto = o.crg === null && !!params;
      const cpaAuto = o.cpa === null && !!params;
      if (!geo) return { ...o, geo, caps, crg, cpa, crgAuto, cpaAuto, hasParams: !!params, status: "empty" };
      const expectedFtds = caps * (crg / 100);
      const payment = expectedFtds * cpa;
      const complete = crg > 0 && cpa > 0;
      return {
        ...o,
        geo,
        caps,
        crg,
        cpa,
        crgAuto,
        cpaAuto,
        hasParams: !!params,
        expectedFtds,
        payment,
        status: complete ? "ok" : "incomplete",
      };
    });
    const validRows = rows.filter((r) => r.status === "ok");
    const totalTraffic = validRows.reduce((s, r) => s + r.payment, 0);
    const net = Math.max(0, totalTraffic - balance);
    const gross = net > 0 ? net / (1 - FEE_RATE) : 0;
    const fee = gross * FEE_RATE;
    const sufficient = totalTraffic > 0 && balance >= totalTraffic;
    return { balance, rows, validRows, totalTraffic, net, gross, fee, sufficient };
  }, [orders, liveBalance, geoParams]);

  const buildCopyText = () => {
    const lines = [];
    lines.push("PREPAYMENT CALCULATION");
    lines.push("");
    lines.push(`Order date: ${fmtDateLong(orderDate) || "-"}`);
    lines.push(`Live balance: ${fmtCurrency(calc.balance)}`);
    lines.push("");
    lines.push("Orders:");
    calc.validRows.forEach((r) => {
      lines.push("");
      lines.push(`${r.geo} \u2014 ${fmtNum(r.caps)} caps`);
      lines.push(`CRG: ${r.crg.toFixed(2)}%`);
      lines.push(`CPA: ${fmtCurrency(r.cpa)}`);
      lines.push(`Expected FTDs: ${fmtNum(r.expectedFtds)}`);
      lines.push(`Payment needed: ${fmtCurrency(r.payment)}`);
    });
    lines.push("");
    lines.push(`Total traffic payment needed: ${fmtCurrency(calc.totalTraffic)}`);
    lines.push(`Live balance: -${fmtCurrency(calc.balance).replace("-", "")}`);
    lines.push(`Net payment needed: ${fmtCurrency(calc.net)}`);
    lines.push(`Fee (2.5%): ${fmtCurrency(calc.fee)}`);
    lines.push(`TOTAL TO PAY/SEND: ${fmtCurrency(calc.gross)}`);
    if (calc.sufficient) {
      lines.push("");
      lines.push("Live balance is sufficient for this order.");
    }
    return lines.join("\n");
  };

  const copyResult = async () => {
    const text = buildCopyText();
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const knownGeos = Object.keys(geoParams);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="prepayment-calculator-dialog"
        className="max-h-[88vh] max-w-xl overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>Prepayment Calculator</DialogTitle>
          <DialogDescription>
            Estimation tool only — nothing is saved and no balances are affected. CRG and CPA are prefilled
            from your Brand data when available (marked "auto") and are fully editable per order.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Inputs */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Order date</label>
              <input
                type="date"
                value={orderDate}
                data-testid="calc-order-date-input"
                onChange={(e) => setOrderDate(e.target.value)}
                className={inputClass}
              />
              {!orderDate && <p className="text-xs text-red-600">Order date is required.</p>}
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Live balance ($)</label>
              <input
                value={liveBalance}
                placeholder="0.00"
                inputMode="decimal"
                data-testid="calc-live-balance-input"
                onChange={(e) => setLiveBalance(e.target.value)}
                className={cn(inputClass, "text-right")}
              />
            </div>
          </div>

          {/* Orders */}
          <div>
            <p className="mb-1.5 text-sm font-semibold">Orders</p>
            <div className="space-y-2">
              {calc.rows.map((row, idx) => (
                <div key={row.id} className="rounded-md border border-border p-2">
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-medium text-muted-foreground">Geo</label>
                      <input
                        value={orders[idx]?.geo ?? ""}
                        placeholder="Type any Geo"
                        data-testid={`calc-order-${idx}-geo-input`}
                        onChange={(e) => setOrderField(row.id, "geo", e.target.value.toUpperCase())}
                        className={cn(inputClass, "w-28 uppercase")}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-medium text-muted-foreground">Ordered caps</label>
                      <input
                        value={orders[idx]?.caps ?? ""}
                        placeholder="0"
                        inputMode="decimal"
                        data-testid={`calc-order-${idx}-caps-input`}
                        onChange={(e) => setOrderField(row.id, "caps", e.target.value)}
                        className={cn(inputClass, "w-20 text-right")}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-medium text-muted-foreground">
                        CRG %{row.crgAuto && <span className="ml-1 text-[10px] font-semibold text-primary">auto</span>}
                      </label>
                      <input
                        value={orders[idx]?.crg !== null ? orders[idx]?.crg : row.hasParams ? row.crg : ""}
                        placeholder="0"
                        inputMode="decimal"
                        data-testid={`calc-order-${idx}-crg-input`}
                        onChange={(e) => setOrderField(row.id, "crg", e.target.value)}
                        className={cn(inputClass, "w-20 text-right")}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-medium text-muted-foreground">
                        CPA ${row.cpaAuto && <span className="ml-1 text-[10px] font-semibold text-primary">auto</span>}
                      </label>
                      <input
                        value={orders[idx]?.cpa !== null ? orders[idx]?.cpa : row.hasParams ? row.cpa : ""}
                        placeholder="0"
                        inputMode="decimal"
                        data-testid={`calc-order-${idx}-cpa-input`}
                        onChange={(e) => setOrderField(row.id, "cpa", e.target.value)}
                        className={cn(inputClass, "w-24 text-right")}
                      />
                    </div>
                    {row.status === "ok" && (
                      <div className="flex flex-1 flex-wrap items-center justify-end gap-x-4 gap-y-1 text-xs text-muted-foreground">
                        <span data-testid={`calc-order-${idx}-expected-ftds`}>Exp. FTDs <span className="font-mono text-foreground">{fmtNum(row.expectedFtds)}</span></span>
                        <span data-testid={`calc-order-${idx}-payment`}>Payment <span className="font-mono font-medium text-foreground">{fmtCurrency(row.payment)}</span></span>
                      </div>
                    )}
                  </div>
                  {row.status === "incomplete" && !row.hasParams && (
                    <p
                      data-testid={`calc-order-${idx}-unavailable`}
                      className="mt-1.5 flex items-start gap-1.5 text-xs text-amber-600"
                    >
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>
                        No existing CRG/CPA configuration was found for this Geo. You can: check another Geo,
                        enter CRG and CPA manually above, or leave this order out.
                      </span>
                    </p>
                  )}
                  {row.status === "incomplete" && row.hasParams && (
                    <p className="mt-1.5 text-xs text-amber-600">
                      Enter a CRG and CPA greater than 0 to include this order.
                    </p>
                  )}
                  {row.status === "empty" && (
                    <p className="mt-1.5 text-xs text-muted-foreground">Type a Geo for this order (free text — any Geo is accepted).</p>
                  )}
                </div>
              ))}
            </div>
            <Button
              variant="secondary"
              size="sm"
              className="mt-2"
              onClick={addOrder}
              data-testid="calc-add-order-button"
            >
              <Plus className="mr-1.5 h-4 w-4" />
              Add Order
            </Button>
            {loadingParams && <p className="mt-1 text-xs text-muted-foreground">Loading Geo parameters…</p>}
            {!loadingParams && knownGeos.length === 0 && (
              <p className="mt-1 text-xs text-muted-foreground">
                No saved Geo data in your Brands yet — enter CRG and CPA manually per order.
              </p>
            )}
          </div>

          {/* Result */}
          <div className="rounded-md border border-border bg-muted/40 p-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Prepayment Calculation
            </p>
            <div className="space-y-1 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Total traffic payment needed</span>
                <span data-testid="calc-total-traffic" className="font-mono font-medium tabular-nums">
                  {fmtCurrency(calc.totalTraffic)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Less live balance</span>
                <span data-testid="calc-less-balance" className="font-mono tabular-nums text-red-600">
                  -{fmtCurrency(calc.balance).replace("-", "")}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Net payment needed</span>
                <span data-testid="calc-net-needed" className="font-mono font-medium tabular-nums">
                  {fmtCurrency(calc.net)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Fee (2.5%)</span>
                <span data-testid="calc-fee" className="font-mono tabular-nums">
                  {fmtCurrency(calc.fee)}
                </span>
              </div>
              <div className="mt-1 flex items-center justify-between border-t border-border pt-2">
                <span className="font-semibold">TOTAL TO PAY / SEND</span>
                <span data-testid="calc-total-to-send" className="font-mono text-lg font-semibold tabular-nums text-accent-foreground">
                  {fmtCurrency(calc.gross)}
                </span>
              </div>
              {calc.sufficient && (
                <p data-testid="calc-balance-sufficient" className="pt-1 text-xs font-medium text-emerald-600">
                  Live balance is sufficient for this order.
                </p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={clearAll} data-testid="calc-clear-button">
              <Eraser className="mr-1.5 h-4 w-4" />
              Clear
            </Button>
            <Button onClick={copyResult} data-testid="calc-copy-button">
              {copied ? <Check className="mr-1.5 h-4 w-4" /> : <Copy className="mr-1.5 h-4 w-4" />}
              {copied ? "Copied!" : "Copy"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
